/**
 * SupplierModal — COM-01: Crear / Editar Proveedor
 */
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Loader2, Globe2 } from 'lucide-react'
import type { Supplier } from '../hooks/usePurchases'
import { inputCls } from '@/lib/formStyles'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { tenantCurrency } from '@/lib/locale'
import { useEtiquetaAsociada } from '@/lib/etiquetaAsociada'
import { Modal } from '@/components/ds/Modal'

interface Props {
  supplier?: Supplier | null
  onSave: (data: Partial<Supplier>) => void
  onClose: () => void
  isPending: boolean
  error?: string | null
  readOnly?: boolean
  /** Mientras llega el detalle del proveedor: el formulario no se puede guardar. */
  loading?: boolean
}

type FormData = {
  legal_name: string
  trade_name: string
  tax_id: string
  tax_id_type: 'RUT' | 'EIN' | 'VAT' | 'OTHER'
  person_type: 'juridica' | 'natural'
  is_international: boolean
  country: string
  default_currency: string
  default_expense_account: string
  default_payable_account: string
  email_identifiers_raw: string  // UI: string separado por comas/saltos; normalizado en submit
  contact_name: string
  email: string
  phone: string
  address_street: string
  address_commune: string
  address_city: string
  address_region: string
  giro: string
  website: string
  notes: string
}

function Field({ label, children, error, htmlFor }: {
  label: string; children: React.ReactNode; error?: string; htmlFor?: string
}) {
  // El id lo genera el envoltorio y viaja al control, así la etiqueta rotula
  // de verdad en vez de quedar de hermana suelta. Ver `etiquetaAsociada`.
  const { htmlFor: id, campo } = useEtiquetaAsociada(children, htmlFor)
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-content-muted mb-1">{label}</label>
      {campo}
      {error && <p className="text-xs text-danger-solid mt-0.5">{error}</p>}
    </div>
  )
}

export function SupplierModal({ supplier, onSave, onClose, isPending, error, readOnly = false, loading = false }: Props) {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      legal_name: '',
      trade_name: '',
      tax_id: '',
      tax_id_type: 'RUT',
      person_type: 'juridica',
      is_international: false,
      country: '',
      default_currency: tenantCurrency(),
      default_expense_account: '',
      default_payable_account: '',
      email_identifiers_raw: '',
      contact_name: '',
      email: '',
      phone: '',
      address_street: '',
      address_commune: '',
      address_city: '',
      address_region: '',
      giro: '',
      website: '',
      notes: '',
    },
  })

  const isInternational = useWatch({ control, name: 'is_international' })
  const { data: accountsData } = useAccounts({
    is_imputable: 'true', is_active: 'true',
  })
  const accounts = accountsData?.accounts ?? []
  const expenseAccounts = accounts.filter(a => a.account_type === 'expense')
  const liabilityAccounts = accounts.filter(a => a.account_type === 'liability')

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    if (supplier) {
      reset({
        legal_name: supplier.legal_name,
        trade_name: supplier.trade_name,
        tax_id: supplier.tax_id,
        tax_id_type: supplier.tax_id_type ?? 'RUT',
        person_type: supplier.person_type,
        is_international: supplier.is_international ?? false,
        country: supplier.country ?? '',
        default_currency: supplier.default_currency ?? 'CLP',
        default_expense_account: supplier.default_expense_account ?? '',
        default_payable_account: supplier.default_payable_account ?? '',
        email_identifiers_raw: (supplier.email_identifiers ?? []).join(', '),
        contact_name: supplier.contact_name,
        email: supplier.email,
        phone: supplier.phone,
        address_street: supplier.address_street,
        address_commune: supplier.address_commune,
        address_city: supplier.address_city,
        address_region: supplier.address_region,
        giro: supplier.giro,
        website: supplier.website,
        notes: supplier.notes,
      })
    }
  }, [supplier, reset])

  const onSubmit = (raw: FormData) => {
    // Sin el detalle, guardar crearía un proveedor nuevo o pisaría el existente
    // con los valores por defecto del formulario.
    if (loading) return
    const email_identifiers = raw.email_identifiers_raw
      .split(/[\n,]+/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean)
    const payload: Partial<Supplier> = {
      legal_name: raw.legal_name,
      trade_name: raw.trade_name,
      tax_id: raw.tax_id,
      tax_id_type: raw.tax_id_type,
      person_type: raw.person_type,
      is_international: raw.is_international,
      country: raw.country,
      default_currency: raw.default_currency,
      default_expense_account: raw.default_expense_account || null,
      default_payable_account: raw.default_payable_account || null,
      email_identifiers,
      contact_name: raw.contact_name,
      email: raw.email,
      phone: raw.phone,
      address_street: raw.address_street,
      address_commune: raw.address_commune,
      address_city: raw.address_city,
      address_region: raw.address_region,
      giro: raw.giro,
      website: raw.website,
      notes: raw.notes,
    }
    onSave(payload)
  }

  return (
    <Modal open onClose={onClose} title={readOnly ? 'Detalle del Proveedor' : supplier ? 'Editar Proveedor' : 'Nuevo Proveedor'} size="2xl">
      {/* Body */}
      <form onSubmit={readOnly ? e => e.preventDefault() : handleSubmit(onSubmit)} className="space-y-4">
        <fieldset disabled={readOnly || loading} className="contents">
        {error && (
          <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content">
            {error}
          </div>
        )}

        {/* Toggle Proveedor internacional */}
        <label className="flex items-start gap-3 bg-primary-50/50 border border-primary-200 rounded-lg p-3 cursor-pointer">
          <input
            type="checkbox"
            {...register('is_international')}
            className="mt-0.5 rounded border-divider-strong text-primary focus:ring-primary/30"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-sm font-medium text-content">
              <Globe2 size={14} className="text-primary" />
              Proveedor internacional
            </div>
            <p className="text-xs text-content-muted mt-0.5">
              Sin RUT chileno. Habilita el flujo de invoices automáticas (PDF por email → voucher) y permite definir cuentas default por partner.
            </p>
          </div>
        </label>

        {/* Bloque internacional */}
        {isInternational && (
          <div className="border border-primary-100 rounded-lg p-4 space-y-3 bg-primary-50/20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Tipo de ID tributario">
                <select {...register('tax_id_type')} className={inputCls}>
                  <option value="RUT">RUT (Chile)</option>
                  <option value="EIN">EIN (USA)</option>
                  <option value="VAT">VAT (UE / UK)</option>
                  <option value="OTHER">Otro</option>
                </select>
              </Field>
              <Field label="País (ISO-2)">
                <input {...register('country')} className={inputCls} placeholder="US" maxLength={2} />
              </Field>
              <Field label="Moneda por defecto">
                <select {...register('default_currency')} className={inputCls}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="JPY">JPY</option>
                  <option value="CLP">CLP</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Cuenta de costo (DEBE al contabilizar)">
                <select aria-label="Usar default del sistema" {...register('default_expense_account')} className={inputCls}>
                  <option value="">— Usar default del sistema —</option>
                  {expenseAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Cuenta por pagar (HABER)">
                <select aria-label="Usar default del sistema" {...register('default_payable_account')} className={inputCls}>
                  <option value="">— Usar default del sistema —</option>
                  {liabilityAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Emails/dominios para auto-matching">
              <textarea
                {...register('email_identifiers_raw')}
                rows={2}
                className={inputCls}
                placeholder="billing@freshworks.com, freshworks.com"
              />
              <p className="text-xs text-content-subtle mt-1">
                Separa por coma o salto de línea. Los invoices que lleguen desde cualquiera de estos emails/dominios se asignarán automáticamente a este proveedor.
              </p>
            </Field>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field label="Razón Social *" error={errors.legal_name?.message}>
              <input {...register('legal_name', { required: 'Campo requerido' })}
                className={inputCls} placeholder="Empresa Distribuidora SpA" />
            </Field>
          </div>
          <Field label="Nombre de Fantasía">
            <input {...register('trade_name')} className={inputCls} placeholder="Nombre comercial" />
          </Field>
          <Field label="RUT / ID Tributario" error={errors.tax_id?.message}>
            <input {...register('tax_id')} className={inputCls} placeholder="76.354.771-K" />
          </Field>
          <Field label="Tipo de Persona">
            <select {...register('person_type')} className={inputCls}>
              <option value="juridica">Persona Jurídica</option>
              <option value="natural">Persona Natural</option>
            </select>
          </Field>
          <Field label="Nombre de Contacto">
            <input {...register('contact_name')} className={inputCls} placeholder="Juan Pérez" />
          </Field>
          <Field label="Email">
            <input {...register('email')} type="email" className={inputCls} placeholder="contacto@proveedor.cl" />
          </Field>
          <Field label="Teléfono">
            <input {...register('phone')} className={inputCls} placeholder="+56 9 1234 5678" />
          </Field>
          <div className="col-span-2">
            <Field label="Dirección">
              <input {...register('address_street')} className={inputCls} placeholder="Av. Principal 123" />
            </Field>
          </div>
          <Field label="Comuna">
            <input {...register('address_commune')} className={inputCls} placeholder="Las Condes" />
          </Field>
          <Field label="Ciudad">
            <input {...register('address_city')} className={inputCls} placeholder="Santiago" />
          </Field>
          <Field label="Región">
            <input {...register('address_region')} className={inputCls} placeholder="Región Metropolitana" />
          </Field>
          <Field label="Giro Comercial">
            <input {...register('giro')} className={inputCls} placeholder="Distribución mayorista de equipos" />
          </Field>
          <Field label="Sitio Web">
            <input {...register('website')} className={inputCls} placeholder="https://proveedor.cl" />
          </Field>
          <div className="col-span-2">
            <Field label="Notas">
              <textarea {...register('notes')} rows={2} className={inputCls} placeholder="Notas internas..." />
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end pt-2">
          {readOnly ? (
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
              Cerrar
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={isPending || loading}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                {(isPending || loading) && <Loader2 size={13} className="animate-spin" />}
                {loading ? 'Cargando…' : supplier ? 'Guardar Cambios' : 'Crear Proveedor'}
              </button>
            </>
          )}
        </div>
        </fieldset>
      </form>
    </Modal>
  )
}
